import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { ThemedSelect } from '../components/ThemedSelect';
import {
  getPublicProfessional,
  listPublicReviewsForPro,
  type PublicProfessional,
  type PublicReview,
  type PublicReviewSort,
} from '../../lib/booking-api';
import { toast } from '../../lib/toast';

const PAGE_SIZE = 10;

const SORT_OPTIONS: { value: PublicReviewSort; label: string }[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'rating_desc', label: 'Highest rated' },
  { value: 'rating_asc', label: 'Lowest rated' },
];

const RATING_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All ratings' },
  { value: '5', label: '5 stars' },
  { value: '4', label: '4 stars' },
  { value: '3', label: '3 stars' },
  { value: '2', label: '2 stars' },
  { value: '1', label: '1 star' },
];

const formatReviewDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '';
  }
};

const composeName = (
  first: string | null | undefined,
  last: string | null | undefined,
  fallback = 'A Cherry Picked customer',
): string => {
  const parts = [first, last].filter(
    (x): x is string => !!x && x.trim() !== '',
  );
  return parts.join(' ').trim() || fallback;
};

// Builds a compact page list with ellipses around the current page so a
// pro with 40+ pages doesn't render 40 buttons in a row.
function buildPageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

export default function PublicReviewsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proId = searchParams.get('id') ?? null;

  const [pro, setPro] = useState<PublicProfessional | null>(null);
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PublicReviewSort>('date_desc');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!proId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getPublicProfessional(proId);
        if (alive) setPro(res.info);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          'Could not load this professional.';
        if (alive) toast.error(msg);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [proId]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!proId) return;
      setReviewsLoading(true);
      try {
        const res = await listPublicReviewsForPro(proId, {
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          sort,
          rating:
            ratingFilter === 'all' ? null : parseInt(ratingFilter, 10),
        });
        if (alive) {
          setReviews(res.data ?? []);
          setTotal(res.total_count ?? 0);
        }
      } catch {
        // Non-critical — keep the existing list visible.
      } finally {
        if (alive) setReviewsLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [proId, page, sort, ratingFilter]);

  // Reset to page 1 when filters change so the user doesn't end up on
  // an out-of-range page after switching ratings.
  useEffect(() => {
    setPage(1);
  }, [sort, ratingFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
    [total],
  );
  const pageList = useMemo(
    () => buildPageList(page, totalPages),
    [page, totalPages],
  );

  const proName = pro
    ? composeName(pro.user?.first_name, pro.user?.last_name, pro.business_name ?? 'Professional')
    : '';

  if (!proId) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <MainHeader />
        <main className="flex-1 max-w-[760px] mx-auto px-4 py-16 text-center">
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
            No professional selected.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[960px] mx-auto px-4 py-8 w-full">
        <button
          type="button"
          onClick={() =>
            navigate(`/public-profile?id=${encodeURIComponent(proId)}`)
          }
          className="inline-flex items-center gap-1 mb-6 text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[14px] hover:underline"
        >
          <span className="material-icons text-[20px]">navigate_before</span>
          Back to profile
        </button>

        <div className="mb-8">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] leading-[36px] mb-2">
            {loading
              ? 'Customer reviews'
              : proName
              ? `Reviews for ${proName}`
              : 'Customer reviews'}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
            {total} review{total === 1 ? '' : 's'} in total
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1">
            <label className="block font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] font-bold uppercase tracking-wider mb-1.5">
              Sort by
            </label>
            <ThemedSelect
              value={sort}
              onChange={(v) => setSort(v as PublicReviewSort)}
              options={SORT_OPTIONS}
              buttonClassName="w-full"
            />
          </div>
          <div className="flex-1">
            <label className="block font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] font-bold uppercase tracking-wider mb-1.5">
              Filter by rating
            </label>
            <ThemedSelect
              value={ratingFilter}
              onChange={(v) => setRatingFilter(v)}
              options={RATING_OPTIONS}
              buttonClassName="w-full"
            />
          </div>
        </div>

        {reviewsLoading && reviews.length === 0 ? (
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] py-12 text-center">
            Loading reviews…
          </p>
        ) : reviews.length === 0 ? (
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] py-12 text-center">
            {ratingFilter === 'all'
              ? 'No reviews yet.'
              : 'No reviews match this filter.'}
          </p>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => {
              const reviewerName = composeName(
                review.customer?.first_name,
                review.customer?.last_name,
              );
              return (
                <div
                  key={review.id}
                  className="border border-[#f0f0f0] rounded-[16px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-8 pb-6">
                    {review.service?.name ? (
                      <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[17px] mb-4">
                        {review.service.name}
                      </h3>
                    ) : null}
                    <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[24px]">
                      {review.comment ?? 'No comment left.'}
                    </p>
                  </div>
                  <div className="bg-[#8e2a2f] p-6 px-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-[40px] h-[40px] rounded-[8px] bg-white/10 flex items-center justify-center border border-white/20">
                        <span className="material-icons text-white">
                          person
                        </span>
                      </div>
                      <div>
                        <p className="font-['DM_Sans',sans-serif] font-bold text-white text-[15px]">
                          {reviewerName}
                        </p>
                        <p className="font-['DM_Sans',sans-serif] text-white/60 text-[13px]">
                          {formatReviewDate(review.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-md">
                      <span className="material-icons text-[#e8cd96] text-[18px]">
                        star
                      </span>
                      <span className="font-['DM_Sans',sans-serif] font-bold text-white text-[14px]">
                        {Number(review.rating).toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 ? (
          <div className="flex items-center justify-center gap-2 pt-8 flex-wrap">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-10 px-4 bg-white border border-[#ede8e9] rounded-[8px] flex items-center font-['DM_Sans',sans-serif] font-bold text-[#908e8e] hover:text-[#3a3335] hover:bg-[#fcfaf7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <span className="material-icons text-[18px] mr-1">
                chevron_left
              </span>
              Prev
            </button>

            {pageList.map((p, idx) =>
              p === '…' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="w-6 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`w-10 h-10 rounded-[8px] flex items-center justify-center font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors ${
                    p === page
                      ? 'bg-[#8e2a2f] text-white'
                      : 'text-[#908e8e] hover:bg-[#fcfaf7] hover:text-[#3a3335]'
                  }`}
                >
                  {p}
                </button>
              ),
            )}

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="h-10 px-4 bg-white border border-[#ede8e9] rounded-[8px] flex items-center font-['DM_Sans',sans-serif] font-bold text-[#908e8e] hover:text-[#3a3335] hover:bg-[#fcfaf7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <span className="material-icons text-[18px] ml-1">
                chevron_right
              </span>
            </button>
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
